import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import {
  getAuth, GoogleAuthProvider, signInWithPopup, signInWithCredential,
  signInAnonymously, linkWithPopup,
  signOut, onAuthStateChanged, User, Auth,
} from 'firebase/auth';
import {
  getFirestore, collection, doc, setDoc, getDoc, getDocs,
  updateDoc, deleteDoc, onSnapshot, query, where, orderBy,
  runTransaction, increment, deleteField,
  initializeFirestore, persistentLocalCache, persistentMultipleTabManager,
  Firestore,
} from 'firebase/firestore';
import type { Task, Project, ProjectFolder, Session, Settings, UserProfile, ForestTree, CloudTimer, MaxerState, DEFAULT_SETTINGS } from './types';
import { DEFAULT_SETTINGS as _DEFAULT_SETTINGS } from './types';

const firebaseConfig = {
  apiKey: 'AIzaSyBkWZz3GTviJAwhwWLOhy-wIhs79OuTbrU',
  authDomain: 'focus-to-do-millan.firebaseapp.com',
  projectId: 'focus-to-do-millan',
  storageBucket: 'focus-to-do-millan.firebasestorage.app',
  messagingSenderId: '475179604545',
  appId: '1:475179604545:web:6d19b253491364c9fc44c0',
  measurementId: 'G-L71WKD39CT',
};

let app: FirebaseApp;
let auth: Auth;
let db: Firestore;

export function initFirebase() {
  if (getApps().length === 0) {
    app = initializeApp(firebaseConfig);
    db = initializeFirestore(app, {
      // Optional task/project fields (dueDate, projectId, folderId, …) are often
      // undefined; without this, Firestore throws and the write is rejected —
      // which is why adding tasks/projects silently failed.
      ignoreUndefinedProperties: true,
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
  } else {
    app = getApps()[0];
    db = getFirestore(app);
  }
  auth = getAuth(app);
  return { app, auth, db };
}

export function getFirebaseInstances() {
  if (!app) initFirebase();
  return { app, auth, db };
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export async function signInWithGoogle(): Promise<User> {
  const { auth } = getFirebaseInstances();
  const provider = new GoogleAuthProvider();
  const current = auth.currentUser;
  // If the user was using the app anonymously, link Google to that account so
  // their local/anonymous data is preserved under the same uid.
  if (current?.isAnonymous) {
    try {
      const res = await linkWithPopup(current, provider);
      return res.user;
    } catch (e: any) {
      // That Google account already exists → sign into it, bringing the guest's tasks and projects
      // along (before they stayed behind under the anonymous uid). Uses the credential from the
      // error, so there's no second popup (iOS blocks it without a tap).
      if (e?.code === 'auth/credential-already-in-use' || e?.code === 'auth/email-already-in-use') {
        const carry = await readGuestData(current.uid).catch(() => null);
        const cred = GoogleAuthProvider.credentialFromError(e);
        const res = cred ? await signInWithCredential(auth, cred) : await signInWithPopup(auth, provider);
        if (carry) await writeGuestData(res.user.uid, carry).catch((err) => console.warn('[auth] no se pudieron traer los datos del invitado', err));
        return res.user;
      }
      throw e;
    }
  }
  const result = await signInWithPopup(auth, provider);
  return result.user;
}

const GUEST_COLLECTIONS = ['tasks', 'projects', 'folders'] as const;
type GuestData = Record<string, { id: string; data: Record<string, unknown> }[]>;

async function readGuestData(uid: string): Promise<GuestData> {
  const { db } = getFirebaseInstances();
  const out: GuestData = {};
  for (const c of GUEST_COLLECTIONS) {
    const snap = await getDocs(collection(db, 'users', uid, c));
    out[c] = snap.docs.map((d) => ({ id: d.id, data: d.data() }));
  }
  return out;
}

async function writeGuestData(uid: string, data: GuestData) {
  const { db } = getFirebaseInstances();
  for (const c of GUEST_COLLECTIONS) {
    for (const d of data[c] ?? []) {
      await setDoc(doc(db, 'users', uid, c, d.id), { ...d.data, ...(d.data.uid ? { uid } : {}) }, { merge: true });
    }
  }
}

/** Use the app without an account (real uid + Firestore offline, no Google). */
export async function signInAnon(): Promise<User> {
  const { auth } = getFirebaseInstances();
  if (auth.currentUser) return auth.currentUser;
  const result = await signInAnonymously(auth);
  return result.user;
}

/** Auth via chrome.identity (for Chrome Extension MV3 — avoids CSP issues) */
export async function signInWithGoogleExtension(webClientId: string): Promise<User> {
  const { auth } = getFirebaseInstances();

  const redirectUrl = chrome.identity.getRedirectURL('oauth2');
  const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  authUrl.searchParams.set('client_id', webClientId);
  authUrl.searchParams.set('redirect_uri', redirectUrl);
  authUrl.searchParams.set('response_type', 'token');
  authUrl.searchParams.set('scope', 'openid email profile');
  authUrl.searchParams.set('prompt', 'select_account');

  return new Promise((resolve, reject) => {
    chrome.identity.launchWebAuthFlow(
      { url: authUrl.toString(), interactive: true },
      async (responseUrl) => {
        if (chrome.runtime.lastError || !responseUrl) {
          reject(new Error(chrome.runtime.lastError?.message ?? 'Auth cancelled'));
          return;
        }
        const hash = new URL(responseUrl).hash.slice(1);
        const accessToken = new URLSearchParams(hash).get('access_token');
        if (!accessToken) { reject(new Error('No access token')); return; }
        try {
          const credential = GoogleAuthProvider.credential(null, accessToken);
          const result = await signInWithCredential(auth, credential);
          resolve(result.user);
        } catch (e) { reject(e); }
      }
    );
  });
}

export async function signOutUser(): Promise<void> {
  const { auth } = getFirebaseInstances();
  await signOut(auth);
}

export function onAuth(callback: (user: User | null) => void) {
  const { auth } = getFirebaseInstances();
  return onAuthStateChanged(auth, callback);
}

// ─── User profile ─────────────────────────────────────────────────────────────

export async function upsertUserProfile(uid: string, partial: Partial<UserProfile>) {
  const { db } = getFirebaseInstances();
  const ref = doc(db, 'users', uid, 'profile', 'data');
  await setDoc(ref, partial, { merge: true });
}

export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const { db } = getFirebaseInstances();
  const snap = await getDoc(doc(db, 'users', uid, 'profile', 'data'));
  return snap.exists() ? (snap.data() as UserProfile) : null;
}

// ─── Settings ─────────────────────────────────────────────────────────────────

export async function saveSettings(uid: string, settings: Settings) {
  const { db } = getFirebaseInstances();
  await setDoc(doc(db, 'users', uid, 'settings', 'main'), settings);
}

export async function getSettings(uid: string): Promise<Settings> {
  const { db } = getFirebaseInstances();
  const snap = await getDoc(doc(db, 'users', uid, 'settings', 'main'));
  return snap.exists() ? (snap.data() as Settings) : _DEFAULT_SETTINGS;
}

export function onSettings(uid: string, callback: (s: Settings | null) => void, onError?: (e: Error) => void) {
  const { db } = getFirebaseInstances();
  return onSnapshot(doc(db, 'users', uid, 'settings', 'main'), snap => {
    callback(snap.exists() ? (snap.data() as Settings) : null);
  }, onError);
}

// ─── Projects ─────────────────────────────────────────────────────────────────

export async function saveProject(uid: string, project: Project) {
  const { db } = getFirebaseInstances();
  await setDoc(doc(db, 'users', uid, 'projects', project.id), project);
}

export async function deleteProject(uid: string, projectId: string) {
  const { db } = getFirebaseInstances();
  await deleteDoc(doc(db, 'users', uid, 'projects', projectId));
}

export function onProjects(uid: string, callback: (projects: Project[]) => void) {
  const { db } = getFirebaseInstances();
  // Single-field orderBy only (no composite index needed); filter archived client-side.
  const q = query(
    collection(db, 'users', uid, 'projects'),
    orderBy('createdAt', 'asc')
  );
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => d.data() as Project).filter(p => !p.archived));
  });
}

// ─── Tasks ────────────────────────────────────────────────────────────────────

// Jaime's tasks are mirrored with Google Tasks by n8n. After a change reaches the server the app
// pings the sync so it shows up in Google in ~1 s (Google -> FlowFocus is polled every 15 s).
const TASKS_SYNC_UID = 'QETxdCv918frrBccaxw1fBOnuNd2';
const TASKS_SYNC_URL = 'https://hermes-n8n.ocgpzm.easypanel.host/webhook/ff-sync';
let tasksSyncTimer: ReturnType<typeof setTimeout> | undefined;
function pingTasksSync(uid: string) {
  if (uid !== TASKS_SYNC_UID || typeof fetch === 'undefined') return;
  clearTimeout(tasksSyncTimer);
  tasksSyncTimer = setTimeout(() => {
    fetch(TASKS_SYNC_URL, { method: 'POST', mode: 'no-cors', keepalive: true }).catch(() => { /* the 5-min sync catches up */ });
  }, 1200);
}

export async function saveTask(uid: string, task: Task) {
  const { db } = getFirebaseInstances();
  await setDoc(doc(db, 'users', uid, 'tasks', task.id), task);
  pingTasksSync(uid);
}

export async function updateTask(uid: string, taskId: string, partial: Partial<Task>) {
  const { db } = getFirebaseInstances();
  // Un undefined explícito significa "quitar el campo" (fecha, recordatorio, completedAt).
  // Con ignoreUndefinedProperties Firestore lo ignoraba y el valor viejo se quedaba.
  const data: Record<string, unknown> = { updatedAt: Date.now() };
  for (const [k, v] of Object.entries(partial)) {
    if (k === 'updatedAt') continue;
    data[k] = v === undefined ? deleteField() : v;
  }
  await updateDoc(doc(db, 'users', uid, 'tasks', taskId), data);
  pingTasksSync(uid);
}

export async function deleteTask(uid: string, taskId: string) {
  const { db } = getFirebaseInstances();
  await deleteDoc(doc(db, 'users', uid, 'tasks', taskId));
}

export function onTasks(uid: string, callback: (tasks: Task[]) => void) {
  const { db } = getFirebaseInstances();
  const q = query(
    collection(db, 'users', uid, 'tasks'),
    orderBy('order', 'asc')
  );
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => d.data() as Task));
  });
}

// ─── Sessions ─────────────────────────────────────────────────────────────────

export async function saveSession(uid: string, session: Session) {
  const { db } = getFirebaseInstances();
  await setDoc(doc(db, 'users', uid, 'sessions', session.id), session);
}

export async function getSessions(uid: string, from: number, to: number): Promise<Session[]> {
  const { db } = getFirebaseInstances();
  const q = query(
    collection(db, 'users', uid, 'sessions'),
    where('startedAt', '>=', from),
    where('startedAt', '<=', to),
    orderBy('startedAt', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data() as Session);
}

// ─── Cloud timer (cross-device) ───────────────────────────────────────────────

export async function saveTimerState(uid: string, state: CloudTimer) {
  const { db } = getFirebaseInstances();
  await setDoc(doc(db, 'users', uid, 'timer', 'state'), state);
}

export function onTimerState(uid: string, callback: (state: CloudTimer | null) => void) {
  const { db } = getFirebaseInstances();
  return onSnapshot(doc(db, 'users', uid, 'timer', 'state'), snap => {
    callback(snap.exists() ? (snap.data() as CloudTimer) : null);
  });
}

// ─── MAXER (fitness) state — read-only, from the unified Firebase ───────────────

export function onMaxerState(uid: string, callback: (state: MaxerState | null) => void) {
  const { db } = getFirebaseInstances();
  return onSnapshot(doc(db, 'users', uid, 'data', 'state'), snap => {
    callback(snap.exists() ? (snap.data() as MaxerState) : null);
  });
}

/**
 * Records a finished work session exactly once across devices: session, tree and the task's
 * pomodoro count go in one transaction, so two devices ending the same session can't double it.
 * Throws when offline (transactions need the server) — callers keep it queued and retry.
 */
export async function recordSessionOnce(
  uid: string,
  session: Session,
  extra?: { tree?: ForestTree; countForTask?: string },
): Promise<boolean> {
  const { db } = getFirebaseInstances();
  const ref = doc(db, 'users', uid, 'sessions', session.id);
  const taskRef = extra?.countForTask ? doc(db, 'users', uid, 'tasks', extra.countForTask) : null;
  return runTransaction(db, async (tx) => {
    if ((await tx.get(ref)).exists()) return false;
    const taskSnap = taskRef ? await tx.get(taskRef) : null;
    tx.set(ref, session);
    if (extra?.tree) tx.set(doc(db, 'users', uid, 'forest', extra.tree.id), extra.tree);
    if (taskRef && taskSnap?.exists()) tx.update(taskRef, { completedPomodoros: increment(1), updatedAt: Date.now() });
    return true;
  });
}

// ─── Forest ───────────────────────────────────────────────────────────────────

export async function saveForestTree(uid: string, tree: ForestTree) {
  const { db } = getFirebaseInstances();
  await setDoc(doc(db, 'users', uid, 'forest', tree.id), tree);
}

export function onForest(uid: string, callback: (trees: ForestTree[]) => void) {
  const { db } = getFirebaseInstances();
  const q = query(
    collection(db, 'users', uid, 'forest'),
    orderBy('grownAt', 'desc')
  );
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => d.data() as ForestTree));
  });
}
